import React, { useState, useEffect, useRef } from 'react';
import emailjs from '@emailjs/browser';
import './Apply.css';
import { FaTimes, FaUser, FaEnvelope, FaPhone, FaGraduationCap, FaPaperPlane } from 'react-icons/fa';

const EnquiryForm = ({ closeModal }) => {
  const formRef = useRef();

  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    course: '',
  });

  const [isFormValid, setIsFormValid] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Email JS credentials
  const SERVICE_ID = "service_6ln5z78"; // Your service ID
  const TEMPLATE_ID = "template_am8u66q"; // Your template ID
  const PUBLIC_KEY = "yj1a4Bp7tRuLCz0cC"; // Your public key

  // Initialize EmailJS once when component mounts
  useEffect(() => {
    emailjs.init(PUBLIC_KEY);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear messages when user starts typing
    setSuccessMessage('');
    setErrorMessage('');
  };

  // Simple, explicit validation instead of relying on HTML5 pattern
  useEffect(() => {
    const allFilled = Object.values(formData).every((v) => v.trim() !== '');
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim());
    const phoneDigitsOnly = formData.phone.replace(/\D/g, '');
    const phoneValid = phoneDigitsOnly.length === 10;

    setIsFormValid(allFilled && emailValid && phoneValid);
  }, [formData]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Extra safety check in case button state is somehow bypassed
    if (!isFormValid) {
      setErrorMessage('Please fill all fields correctly (10-digit phone number required).');
      return;
    }

    setIsSubmitting(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const templateParams = {
        from_name: `${formData.firstname} ${formData.lastname}`,
        from_email: formData.email,
        phone: formData.phone,
        course: formData.course,
        message: `New enquiry from ${formData.firstname} ${formData.lastname}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nCourse: ${formData.course}`,
        to_email: 'ptlrcollegefbd@gmail.com', // College email
        reply_to: formData.email,
      };

      const result = await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams);

      console.log('EmailJS result:', result);

      if (result.status === 200 || result.text === 'OK') {
        setSuccessMessage('Thank you for your enquiry! Our counselor will contact you soon.');
        setErrorMessage('');
        setFormData({ firstname: '', lastname: '', email: '', phone: '', course: '' });

        // Close modal after 3 seconds on success
        setTimeout(() => {
          closeModal();
        }, 3000);
      } else {
        throw new Error('Unexpected response from EmailJS: ' + JSON.stringify(result));
      }
    } catch (err) {
      // This will show up in browser console - check it for the real reason
      console.error('Email JS Error:', err);
      setErrorMessage('Unable to submit form. Please try again or contact us directly.');
      setSuccessMessage('');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Close modal when clicking outside
  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('enquiry-overlay')) {
      closeModal();
    }
  };

  return (
    <div className="enquiry-overlay" onClick={handleOverlayClick}>
      <div className="enquiry-modal">
        {/* Header with gradient */}
        <div className="enquiry-header">
          <h2 className="enquiry-title">
            <span className="title-icon">📋</span>
            Enquiry Form
          </h2>
          <button className="enquiry-close" onClick={closeModal}>
            <FaTimes />
          </button>
        </div>

        {/* Status Messages */}
        {successMessage && (
          <div className="enquiry-message success">
            <span className="message-icon">✓</span>
            {successMessage}
          </div>
        )}

        {errorMessage && (
          <div className="enquiry-message error">
            <span className="message-icon">⚠</span>
            {errorMessage}
          </div>
        )}

        {/* Form: noValidate so our own JS validation controls submit behavior */}
        <form ref={formRef} onSubmit={handleSubmit} className="enquiry-form" noValidate>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstname" className="form-label">
                <FaUser className="input-icon" />
                First Name
              </label>
              <input
                type="text"
                id="firstname"
                name="firstname"
                className="form-input"
                placeholder="Enter your first name"
                value={formData.firstname}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastname" className="form-label">
                <FaUser className="input-icon" />
                Last Name
              </label>
              <input
                type="text"
                id="lastname"
                name="lastname"
                className="form-input"
                placeholder="Enter your last name"
                value={formData.lastname}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email" className="form-label">
              <FaEnvelope className="input-icon" />
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className="form-input"
              placeholder="Enter your email address"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone" className="form-label">
              <FaPhone className="input-icon" />
              Phone Number
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              className="form-input"
              placeholder="Enter your 10-digit phone number"
              value={formData.phone}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="course" className="form-label">
              <FaGraduationCap className="input-icon" />
              Select Course
            </label>
            <select
              id="course"
              name="course"
              className="form-select"
              value={formData.course}
              onChange={handleInputChange}
              required
            >
              <option value="">-- Choose your course --</option>
              <option value="Diploma in Engineering">Diploma in Engineering</option>
              <option value="B.Tech (Bachelor of Technology)">B.Tech (Bachelor of Technology)</option>
              <option value="M.Tech (Master of Technology)">M.Tech (Master of Technology)</option>
              <option value="MBA (Master of Business Administration)">MBA (Master of Business Administration)</option>
            </select>
          </div>

          <button
            type="submit"
            className={`form-submit ${isSubmitting ? 'submitting' : ''}`}
            disabled={!isFormValid || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="spinner"></span>
                Submitting...
              </>
            ) : (
              <>
                <FaPaperPlane className="submit-icon" />
                Submit Enquiry
              </>
            )}
          </button>
        </form>

        {/* Footer Note */}
        <p className="form-footer">
          Our counselor will contact you within 24 hours
        </p>
      </div>
    </div>
  );
};

export default EnquiryForm;
